import type { TaskStatus } from '@obras/shared';
import { TASK_STATUS_LABELS, TASK_STATUSES } from '@obras/shared';
import { useId } from 'react';

import { SingleSelect } from '../../components/ui/MultiSelect.js';
import { cn } from '../../lib/cn.js';
import { APARENCIA_DA_SITUACAO } from './aparencia.js';

export function IconeDaSituacao({
  status,
  size = 18,
  className,
}: {
  status: TaskStatus;
  size?: number | undefined;
  className?: string | undefined;
}) {
  const { icone: Icone, cor } = APARENCIA_DA_SITUACAO[status];
  return <Icone size={size} strokeWidth={1.75} className={cn(cor, className)} aria-hidden />;
}

const OPCOES = TASK_STATUSES.map((status) => ({
  value: status,
  label: TASK_STATUS_LABELS[status],
  icon: <IconeDaSituacao status={status} size={16} />,
}));

/**
 * A situação na linha da lista: o ícone dela, que abre o menu das quatro.
 * Trocar daqui não abre a tarefa — o clique para no menu, sem chegar à linha.
 */
export function MenuDaSituacao({
  status,
  onChange,
}: {
  status: TaskStatus;
  onChange: (status: TaskStatus) => void;
}) {
  return (
    <div onClick={(evento) => evento.stopPropagation()} className="shrink-0">
      <SingleSelect
        label={`Situação: ${TASK_STATUS_LABELS[status]}. Trocar a situação`}
        options={OPCOES}
        value={status}
        onChange={(valor) => onChange(valor as TaskStatus)}
        triggerClassName="grid size-8 place-items-center transition-colors duration-100 hover:bg-sunken-2 focus-visible:bg-sunken-2 focus-visible:outline-hidden aria-expanded:bg-sunken-2"
        renderTrigger={() => <IconeDaSituacao status={status} size={19} />}
      />
    </div>
  );
}

/**
 * A situação no modal: as quatro à vista, uma embaixo da outra, cada uma com o
 * seu ícone. São rádios de verdade — as setas do teclado andam entre elas —,
 * desenhados como linhas: a escolhida fica em `sunken`, com a barra da cor
 * dela à esquerda.
 */
export function EscolhaDaSituacao({
  value,
  onChange,
  emGrade = false,
}: {
  value: TaskStatus;
  onChange: (status: TaskStatus) => void;
  /** Em duas colunas, duas a duas: a mesma escolha em metade da altura. */
  emGrade?: boolean | undefined;
}) {
  const nome = useId();

  return (
    <fieldset>
      <legend className="mb-1.5 text-[13px] font-medium">Situação</legend>
      <div className={emGrade ? 'grid grid-cols-2 gap-px' : 'flex flex-col'}>
        {TASK_STATUSES.map((status) => {
          const escolhida = status === value;
          const id = `${nome}-${status}`;

          return (
            <div key={status} className="relative">
              <input
                id={id}
                type="radio"
                name={nome}
                value={status}
                checked={escolhida}
                onChange={() => onChange(status)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className={cn(
                  'relative flex h-9 cursor-pointer items-center gap-2.5 px-3 text-sm transition-colors duration-100',
                  'peer-focus-visible:outline-2 peer-focus-visible:-outline-offset-2 peer-focus-visible:outline-primary',
                  escolhida ? 'bg-sunken font-medium text-ink' : 'text-ink-2 hover:bg-sunken/60',
                )}
              >
                {escolhida ? (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-y-0 left-0 w-0.5',
                      APARENCIA_DA_SITUACAO[status].barra,
                    )}
                  />
                ) : null}
                <IconeDaSituacao status={status} size={16} />
                {TASK_STATUS_LABELS[status]}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
